import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  ArrowLeft, Shield, HardDrive, Activity, 
  Lock, Settings as SettingsIcon, CheckCircle, XCircle, Terminal, Brain, ShieldCheck, RotateCcw,
  MapPin, Battery, Camera, Bell, Trash2, Smartphone, Wifi, AlertTriangle
} from 'lucide-react';
import { usePermission } from '../../hooks/usePermission';

// --- Helper: Status Toggle Component (Read-Only Visualization) ---
const SettingCard = ({ label, value }) => {
  const isActive = value === 'true' || value === true;
  return (
    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
      <span className="text-sm font-medium text-slate-700 capitalize">{label.replace(/_/g, ' ')}</span>
      <div className={`flex items-center px-3 py-1 rounded-full text-xs font-bold ${
        isActive ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'
      }`}>
        {isActive ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
        {isActive ? 'ENABLED' : 'DISABLED'}
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, tone = 'slate' }) => {
  const toneClass =
    tone === 'green' ? 'text-green-700 bg-green-50 border-green-100' :
    tone === 'red' ? 'text-red-700 bg-red-50 border-red-100' :
    tone === 'orange' ? 'text-orange-700 bg-orange-50 border-orange-100' :
    tone === 'purple' ? 'text-purple-700 bg-purple-50 border-purple-100' :
    tone === 'blue' ? 'text-blue-700 bg-blue-50 border-blue-100' :
    'text-slate-700 bg-slate-50 border-slate-200';

  return (
    <div className={`p-4 rounded-xl border ${toneClass}`}>
      <span className="block text-xs font-bold uppercase opacity-70">
        {label.replace(/_/g, ' ')}
      </span>
      <strong className="block mt-1 text-lg font-black">
        {value ?? '-'}
      </strong>
    </div>
  );
};

// --- Helper: Format raw score values to clean percentages ---
const formatToPercent = (val) => {
  if (val === undefined || val === null || val === '-') return '-';
  const num = Number(val);
  if (isNaN(num)) return val;
  return `${Math.round(num * 100)}%`;
};

// --- Helper: Alert Row Component ---
const AlertRow = ({ alert }) => (
  <tr className="hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0">
    <td className="px-6 py-4">
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${
        alert.severity === 'critical' ? 'bg-red-100 text-red-800' :
        alert.severity === 'high' ? 'bg-orange-100 text-orange-800' :
        'bg-blue-100 text-blue-800'
      }`}>
        {alert.severity}
      </span>
    </td>
    <td className="px-6 py-4">
      <p className="text-sm font-semibold text-slate-700">{alert.rule_name}</p>
      <p className="text-xs text-slate-500 font-mono mt-0.5 truncate max-w-xs">{alert.details}</p>
    </td>
    <td className="px-6 py-4">
       <span className={`text-xs font-medium px-2 py-1 rounded ${
         alert.status === 'neutralized' ? 'bg-green-50 text-green-600' : 
         alert.status === 'new' ? 'bg-red-50 text-red-600' : 'bg-slate-50 text-slate-500'
       }`}>
         {alert.status.toUpperCase()}
       </span>
    </td>
    <td className="px-6 py-4 text-sm text-slate-500">
      {new Date(alert.timestamp).toLocaleString()}
    </td>
  </tr>
);

const DeviceDetailPage = () => {
  const { can } = usePermission();
  const queryClient = useQueryClient();
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [memorySubTab, setMemorySubTab] = useState('confidence');
  const [c2Status, setC2Status] = useState(null);

  const triggerActionMutation = useMutation({
    mutationFn: ({ action, args = {} }) => 
      axiosClient.post(`/admin/agents/${agentId}/action`, { action, args }),
    onSuccess: (data, variables) => {
      setC2Status(`Command '${variables.action}' queued successfully!`);
      setTimeout(() => setC2Status(null), 4000);
      queryClient.invalidateQueries(['device', agentId]);
    },
    onError: (error) => {
      setC2Status(`Failed to queue command: ${error.message}`);
      setTimeout(() => setC2Status(null), 4000);
    }
  });

  const { data: profile, isLoading } = useQuery({
    queryKey: ['device', agentId],
    queryFn: () => axiosClient.get(`/admin/devices/${agentId}/details`)
  });

  const { data: alertsData, isLoading: alertsLoading } = useQuery({
    queryKey: ['deviceAlerts', agentId],
    queryFn: () => axiosClient.get(`/admin/devices/${agentId}/alerts`),
    enabled: activeTab === 'alerts'
  });

  const { data: reputationData, isLoading: reputationLoading } = useQuery({
    queryKey: ['deviceProcessReputation', agentId],
    queryFn: () => axiosClient.get('/memory/process-reputation', {
      params: {
        agent_id: agentId,
      },
    }),
    enabled: activeTab === 'memory',
    staleTime: 30000,
  });

  const { data: rollbackRiskData, isLoading: rollbackRiskLoading } = useQuery({
    queryKey: ['deviceRollbackRisk', agentId],
    queryFn: () => axiosClient.get('/memory/rollback-risk', {
      params: { agent_id: agentId },
    }),
    enabled: activeTab === 'memory',
    staleTime: 30000,
  });

  const { data: deviceReputationDetails, isLoading: deviceReputationDetailsLoading } = useQuery({
    queryKey: ['deviceReputationDetails', agentId],
    queryFn: () => axiosClient.get('/memory/process-reputation/details', {
      params: {
        agent_id: agentId,
        limit: 25,
      },
    }),
    enabled: activeTab === 'memory',
    staleTime: 30000,
  });

  const deviceReputationItems = deviceReputationDetails?.items || [];

  const deviceProcessSummary = {
    safe_score: deviceReputationItems.length
      ? (
          deviceReputationItems.reduce((sum, item) => sum + Number(item.safe_score || 0), 0) /
          deviceReputationItems.length
        ).toFixed(4)
      : 0,
    threat_score: deviceReputationItems.length
      ? Math.max(...deviceReputationItems.map((item) => Number(item.threat_score || 0)))
      : 0,
    rollback_penalty: deviceReputationItems.length
      ? Math.max(...deviceReputationItems.map((item) => Number(item.rollback_penalty || 0)))
      : 0,
    trust_decay_factor: '-',
    seen_count: deviceReputationItems.reduce((sum, item) => sum + Number(item.seen_count || 0), 0),
    label: deviceReputationItems.length ? 'agent_scoped_reputation' : 'unknown',
    source: 'agent_scoped_process_reputation',
    effect: 'context_only',
  };

  const deviceReputationSummary = {
    platform_count: new Set(deviceReputationItems.map((item) => item.platform)).size,
    total_seen: deviceReputationItems.reduce((sum, item) => sum + Number(item.seen_count || 0), 0),
    safe_score: deviceProcessSummary.safe_score,
    threat_score: deviceProcessSummary.threat_score,
    label: deviceReputationItems.length ? 'device_reputation_context' : 'unknown',
    source: 'agent_scoped_process_reputation',
    effect: 'context_only',
  };

  const { data: trainingCandidatesData, isLoading: trainingCandidatesLoading } = useQuery({
    queryKey: ['deviceTrainingCandidates', agentId],
    queryFn: () => axiosClient.get('/memory/training-candidates', {
      params: {
        agent_id: agentId,
        review_status: 'pending',
        limit: 25,
      },
    }),
    enabled: activeTab === 'training',
    staleTime: 30000,
  });

  const reviewTrainingCandidateMutation = useMutation({
    mutationFn: ({ id, review_status, note }) => axiosClient.post(
      `/memory/training-candidates/${id}/review`,
      {
        review_status,
        reviewer: 'admin-ui',
        note,
      }
    ),
    onSuccess: () => {
      queryClient.invalidateQueries(['deviceTrainingCandidates', agentId]);
    },
  });

  const { data: networkBaselineData, isLoading: networkBaselineLoading } = useQuery({
    queryKey: ['deviceNetworkBaselines', agentId],
    queryFn: () => axiosClient.get('/memory/network-baseline', {
      params: {
        agent_id: agentId,
        limit: 25,
      },
    }),
    enabled: activeTab === 'memory',
    staleTime: 30000,
  });

  const { data: fileBaselineData, isLoading: fileBaselineLoading } = useQuery({
    queryKey: ['deviceFileBaselines', agentId],
    queryFn: () => axiosClient.get('/memory/file-baseline', {
      params: {
        agent_id: agentId,
        limit: 25,
      },
    }),
    enabled: activeTab === 'memory',
    staleTime: 30000,
  });

  const { data: behaviorConfidenceData, isLoading: behaviorConfidenceLoading } = useQuery({
    queryKey: ['deviceBehaviorConfidence', agentId],
    queryFn: () => axiosClient.get('/memory/behavior-confidence', {
      params: {
        agent_id: agentId,
        limit: 25,
      },
    }),
    enabled: activeTab === 'memory',
    staleTime: 30000,
  });

  const { data: baselineAnomalyData, isLoading: baselineAnomalyLoading } = useQuery({
    queryKey: ['deviceBaselineAnomaly', agentId],
    queryFn: () => axiosClient.get('/memory/baseline-anomaly', {
      params: {
        agent_id: agentId,
        limit: 25,
      },
    }),
    enabled: activeTab === 'memory',
    staleTime: 30000,
  });

  const { data: clustersData, isLoading: clustersLoading } = useQuery({
    queryKey: ['deviceThreatPatternClusters', agentId],
    queryFn: () => axiosClient.get('/memory/threat-pattern-clusters', {
      params: {
        agent_id: agentId,
        limit: 25,
      },
    }),
    enabled: activeTab === 'memory',
    staleTime: 30000,
  });

  if (isLoading) return <div className="p-10 text-center text-slate-400">Loading Agent Profile...</div>;
  if (!profile) return <div className="p-10 text-center text-red-400">Agent not found.</div>;

  const { info, stats, settings } = profile;


  return (
    <div className="space-y-6 animate-fade-in pb-10">
      
      {/* Back Navigation */}
      <button 
        onClick={() => navigate('/devices')}
        className="flex items-center text-slate-500 hover:text-slate-800 transition-colors text-sm font-medium"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Fleet
      </button>

      {/* Hero Header */}
      <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center relative overflow-hidden">
        <div className={`absolute top-0 left-0 w-1 h-full ${info.status === 'online' ? 'bg-green-500' : 'bg-slate-300'}`}></div>
        
        <div className="flex items-center mb-4 md:mb-0 z-10">
          <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mr-5 shadow-lg shadow-blue-900/20">
            <HardDrive className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{info.hostname}</h1>
            <div className="flex items-center text-sm text-slate-500 mt-1 space-x-4">
               <span className="flex items-center"><Terminal className="w-3 h-3 mr-1" /> {info.os_info || 'Unknown OS'}</span>
               <span className="flex items-center font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">ID: {info.agent_id.substring(0, 64)}</span>
            </div>
          </div>
        </div>
        
        <div className="flex space-x-3 z-10">
          {can('devices:control') && (
            <button className="px-4 py-2 bg-red-50 text-red-600 font-medium rounded-lg hover:bg-red-100 transition-colors flex items-center border border-red-100 text-sm">
              <Lock className="w-4 h-4 mr-2" /> Isolate Host
            </button>
          )}
          <div className={`px-4 py-2 rounded-lg border flex items-center font-medium text-sm ${info.status === 'online' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
             <div className={`w-2 h-2 rounded-full mr-2 ${info.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`}></div>
             {info.status?.toUpperCase()}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex space-x-8">
          {['overview', 'alerts', 'settings', 'memory', 'training'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors capitalize ${
                activeTab === tab 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-6 animate-in slide-in-from-left-4 duration-300">
            {/* Core KPI metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
                  <h3 className="text-slate-500 text-sm font-medium mb-2 flex items-center"><Shield className="w-4 h-4 mr-2 text-blue-500"/> Total Threats</h3>
                  <p className="text-3xl font-black text-slate-800">{stats.threats}</p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
                  <h3 className="text-slate-500 text-sm font-medium mb-2 flex items-center"><Lock className="w-4 h-4 mr-2 text-amber-500"/> Quarantined Files</h3>
                  <p className="text-3xl font-black text-slate-800">{stats.quarantined}</p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
                  <h3 className="text-slate-500 text-sm font-medium mb-2 flex items-center"><Activity className="w-4 h-4 mr-2 text-green-500"/> Connection Quality</h3>
                  <p className="text-3xl font-black text-green-600">Stable</p>
                  <p className="text-xs text-slate-400 mt-1">Last heartbeat: {new Date(info.last_seen_at).toLocaleTimeString()}</p>
                </div>
            </div>

            {/* Android Specific Controls and Vitals */}
            {info.os_info?.toLowerCase().includes('android') && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Mobile Vitals */}
                  <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm space-y-4 col-span-1">
                      <h3 className="text-sm font-bold text-slate-800 flex items-center border-b border-slate-100 pb-3">
                        <Smartphone className="w-4 h-4 mr-2 text-blue-500" />
                        Mobile Vitals
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                            <span className="text-[10px] font-bold text-slate-400 uppercase block">Battery Level</span>
                            <div className="flex items-center mt-1 text-slate-700">
                              <Battery className="w-4 h-4 mr-1 text-green-500" />
                              <span className="font-bold text-sm">{info.battery_level ?? '99'}%</span>
                            </div>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                            <span className="text-[10px] font-bold text-slate-400 uppercase block">RAM Allocated</span>
                            <div className="flex items-center mt-1 text-slate-700">
                              <Activity className="w-4 h-4 mr-1 text-blue-500" />
                              <span className="font-bold text-sm">{info.ram_usage_mb ?? '1932'} MB</span>
                            </div>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                            <span className="text-[10px] font-bold text-slate-400 uppercase block">Network</span>
                            <div className="flex items-center mt-1 text-slate-700">
                              <Wifi className="w-4 h-4 mr-1 text-purple-500" />
                              <span className="font-bold text-sm uppercase">{info.network_type ?? 'WIFI'}</span>
                            </div>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                            <span className="text-[10px] font-bold text-slate-400 uppercase block">Security Score</span>
                            <div className="flex items-center mt-1 text-slate-700">
                              <ShieldCheck className="w-4 h-4 mr-1 text-green-500" />
                              <span className="font-bold text-sm">{info.security_score ?? '95'}/100</span>
                            </div>
                        </div>
                      </div>
                  </div>

                  {/* Map Location */}
                  <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm col-span-1 flex flex-col justify-between">
                      <div>
                        <h3 className="text-sm font-bold text-slate-800 flex items-center border-b border-slate-100 pb-3 mb-4">
                            <MapPin className="w-4 h-4 mr-2 text-red-500" />
                            GPS Location
                        </h3>
                        <div className="text-xs text-slate-600 space-y-1">
                            <p><b>Latitude:</b> {info.latitude ?? '20.0000'}</p>
                            <p><b>Longitude:</b> {info.longitude ?? '77.0000'}</p>
                            <p className="text-slate-400 mt-2">Interactive mapping routes are active.</p>
                        </div>
                      </div>
                      <a 
                        href={`https://www.openstreetmap.org/?mlat=${info.latitude ?? '20.0000'}&mlon=${info.longitude ?? '77.0000'}#map=15`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-xs text-center block transition-colors border border-slate-200"
                      >
                        Open Location on Map
                      </a>
                  </div>

                  {/* Remote Actions */}
                   <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm col-span-1 space-y-4">
                      <h3 className="text-sm font-bold text-slate-800 flex items-center border-b border-slate-100 pb-3">
                         <Lock className="w-4 h-4 mr-2 text-amber-500" />
                         Tactical C2 Actions
                      </h3>
                      {c2Status && (
                         <div className="p-2.5 bg-blue-50 border border-blue-100 text-blue-700 font-bold rounded-lg text-xs">
                           {c2Status}
                         </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <button 
                          onClick={() => {
                            if (window.confirm("Trigger physical siren on this device?")) {
                              triggerActionMutation.mutate({ action: "trigger_alarm" });
                            }
                          }}
                          className="p-3 bg-slate-50 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition-all font-semibold text-xs text-center flex flex-col items-center justify-center"
                        >
                          <Activity className="w-4 h-4 mb-1 text-red-500" />
                          Siren Alarm
                        </button>
                        <button 
                          onClick={() => {
                            if (window.confirm("Are you sure you want to lock this device?")) {
                              triggerActionMutation.mutate({ action: "lock_device" });
                            }
                          }}
                          className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold transition-all flex flex-col items-center justify-center space-y-1 hover:bg-slate-100 text-slate-700"
                        >
                          <Lock className="w-4 h-4 mb-1 text-amber-500" />
                          <span>Lock Screen</span>
                        </button>
                        <button 
                          onClick={() => {
                            if (window.confirm("Trigger stealth front-camera photo capture?")) {
                              triggerActionMutation.mutate({ action: "take_snapshot" });
                            }
                          }}
                          className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold transition-all flex flex-col items-center justify-center space-y-1 hover:bg-slate-100 text-slate-700"
                        >
                          <Camera className="w-4 h-4 mb-1 text-blue-500" />
                          <span>Take Photo</span>
                        </button>
                        <button 
                          onClick={() => {
                             if (window.confirm("⚠️ WARNING: This will trigger a full enterprise wipe. Are you absolutely sure?")) {
                                triggerActionMutation.mutate({ action: "wipe_data" });
                             }
                          }}
                          className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold transition-all flex flex-col items-center justify-center space-y-1 hover:bg-slate-100 text-slate-700"
                        >
                          <Trash2 className="w-4 h-4 mb-1 text-slate-500" />
                          <span>Remote Wipe</span>
                        </button>
                      </div>
                   </div>
                </div>
            )}
          </div>
        )}

        {/* ALERTS TAB (Real Data) */}
        {activeTab === 'alerts' && (
           <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden animate-in slide-in-from-right-4 duration-300">
              {alertsLoading ? (
                 <div className="p-10 text-center text-slate-400">Fetching Security Logs...</div>
              ) : !alertsData?.alerts || alertsData.alerts.length === 0 ? (
                 <div className="p-12 text-center flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                       <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">System Secure</h3>
                    <p className="text-slate-500 text-sm mt-1">No active threats detected on this device.</p>
                 </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase">
                      <th className="px-6 py-4">Severity</th>
                      <th className="px-6 py-4">Detection Rule</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {alertsData.alerts.map(alert => <AlertRow key={alert.id} alert={alert} />)}
                  </tbody>
                </table>
              )}
           </div>
        )}
        
        {/* MEMORY TAB */}
        {activeTab === 'memory' && (
          <div className="space-y-6 animate-in fade-in duration-500">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-800 flex items-center">
                    <Brain className="w-5 h-5 mr-2 text-purple-600" />
                    Memory Intelligence
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Reputation, rollback, baseline, confidence and anomaly context.
                  </p>
                </div>
                <span className="text-[10px] font-bold uppercase px-3 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                  Context Only
                </span>
              </div>

              {reputationLoading || rollbackRiskLoading || deviceReputationDetailsLoading || networkBaselineLoading || fileBaselineLoading || behaviorConfidenceLoading || baselineAnomalyLoading || clustersLoading ? (
                <div className="p-8 text-center text-slate-400">Loading memory intelligence...</div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 border-b border-slate-100 mb-6 pb-3">
                    {[
                      ['confidence', 'Confidence'],
                      ['network', 'Network Baselines'],
                      ['file', 'File Baselines'],
                      ['behavior', 'Behavior Confidence'],
                      ['reputation', 'Fleet Reputation Details'],
                      ['anomaly', 'Baseline Anomaly History'],
                      ['clusters', 'Threat Pattern Clusters'],
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        onClick={() => setMemorySubTab(key)}
                        className={`px-4 py-2 rounded-lg text-xs font-bold border transition-colors ${
                          memorySubTab === key
                            ? 'bg-purple-50 text-purple-700 border-purple-200'
                            : 'bg-white text-slate-500 border-slate-100 hover:bg-slate-50'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {memorySubTab === 'confidence' && (
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                      <div className="rounded-xl border border-slate-100 bg-slate-50 p-5">
                        <h4 className="text-sm font-bold text-slate-700 flex items-center mb-4">
                          <ShieldCheck className="w-4 h-4 mr-2 text-green-600" />
                          Process Reputation
                        </h4>
                        <div className="grid grid-cols-2 gap-3">
                          <MetricCard label="safe_score" value={formatToPercent(reputationData?.safe_score)} tone="green" />
                          <MetricCard label="threat_score" value={formatToPercent(reputationData?.threat_score)} tone="red" />
                          <MetricCard label="rollback_penalty" value={formatToPercent(reputationData?.rollback_penalty)} tone="orange" />
                          <MetricCard label="trust_decay_factor" value={reputationData?.trust_decay_factor ?? 1} tone="purple" />
                        </div>
                        <div className="mt-4 text-xs text-slate-600 space-y-1">
                          <p><b>Label:</b> {reputationData?.label || 'unknown'}</p>
                          <p><b>Seen Count:</b> {reputationData?.seen_count ?? 0}</p>
                          <p><b>Source:</b> {reputationData?.source || 'backend_reputation_empty'}</p>
                          <p><b>Effect:</b> {reputationData?.effect || 'context_only'}</p>
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-100 bg-slate-50 p-5">
                        <h4 className="text-sm font-bold text-slate-700 flex items-center mb-4">
                          <RotateCcw className="w-4 h-4 mr-2 text-orange-600" />
                          Rollback Risk
                        </h4>
                        <div className="grid grid-cols-2 gap-3">
                          <MetricCard label="rollback_risk" value={rollbackRiskData?.rollback_risk || 'unknown'} tone="orange" />
                          <MetricCard label="rollback_count" value={rollbackRiskData?.rollback_count ?? 0} tone="orange" />
                          <MetricCard label="total_outcomes" value={rollbackRiskData?.total_outcomes ?? 0} tone="slate" />
                          <MetricCard label="rollback_rate" value={formatToPercent(rollbackRiskData?.rollback_rate)} tone="purple" />
                        </div>
                        <div className="mt-4 text-xs text-slate-600 space-y-1">
                          <p><b>Source:</b> {rollbackRiskData?.source || 'backend_fleet_memory'}</p>
                          <p><b>Mode:</b> Read-only advisory intelligence.</p>
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-100 bg-slate-50 p-5">
                        <h4 className="text-sm font-bold text-slate-700 flex items-center mb-4">
                          <Activity className="w-4 h-4 mr-2 text-blue-600" />
                          Fleet Reputation
                        </h4>
                        <div className="grid grid-cols-2 gap-3">
                          <MetricCard label="platforms" value={deviceReputationSummary?.platform_count ?? 0} tone="blue" />
                          <MetricCard label="total_seen" value={deviceReputationSummary?.total_seen ?? 0} tone="slate" />
                          <MetricCard label="safe_score" value={formatToPercent(deviceReputationSummary?.safe_score)} tone="green" />
                          <MetricCard label="threat_score" value={formatToPercent(deviceReputationSummary?.threat_score)} tone="red" />
                        </div>
                        <div className="mt-4 text-xs text-slate-600 space-y-1">
                          <p><b>Label:</b> {deviceReputationSummary?.label || 'unknown'}</p>
                          <p><b>Raw Safe Score:</b> {formatToPercent(deviceReputationSummary?.raw_safe_score)}</p>
                          <p><b>Rollback Penalty:</b> {formatToPercent(deviceReputationSummary?.rollback_penalty)}</p>
                          <p><b>Source:</b> {deviceReputationSummary?.source || 'fleet_reputation_federation'}</p>
                          <p><b>Effect:</b> {deviceReputationSummary?.effect || 'context_only'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {memorySubTab === 'network' && (
                    <div className="rounded-xl border border-slate-100 bg-white overflow-hidden">
                      <div className="px-5 py-4 border-b border-slate-100">
                        <h4 className="text-sm font-bold text-slate-700">Network Baselines</h4>
                        <p className="text-xs text-slate-400">Known network behavior for this selected agent.</p>
                      </div>
                      <div className="overflow-auto max-h-[520px]">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                            <tr><th className="px-4 py-3">Process</th><th className="px-4 py-3">Destination</th><th className="px-4 py-3">Score</th><th className="px-4 py-3">Seen</th></tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(networkBaselineData?.items || []).map((item, index) => (
                              <tr key={`${item.process_name}-${item.dest_host}-${item.dest_port}-${index}`}>
                                <td className="px-4 py-3 font-mono">{item.process_name}</td>
                                <td className="px-4 py-3 font-mono">{item.dest_host}:{item.dest_port}</td>
                                <td className="px-4 py-3 font-bold text-blue-700">{formatToPercent(item.baseline_score)}</td>
                                <td className="px-4 py-3">{item.seen_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {(networkBaselineData?.items || []).length === 0 && <div className="p-6 text-center text-slate-400">No network baselines found.</div>}
                      </div>
                    </div>
                  )}

                  {memorySubTab === 'file' && (
                    <div className="rounded-xl border border-slate-100 bg-white overflow-hidden">
                      <div className="px-5 py-4 border-b border-slate-100">
                        <h4 className="text-sm font-bold text-slate-700">File Baselines</h4>
                        <p className="text-xs text-slate-400">Known file activity for this selected agent.</p>
                      </div>
                      <div className="overflow-auto max-h-[520px]">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                            <tr><th className="px-4 py-3">Process</th><th className="px-4 py-3">Path</th><th className="px-4 py-3">Op</th><th className="px-4 py-3">Score</th></tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(fileBaselineData?.items || []).map((item, index) => (
                              <tr key={`${item.process_name}-${item.path_bucket}-${item.operation}-${index}`}>
                                <td className="px-4 py-3 font-mono">{item.process_name}</td>
                                <td className="px-4 py-3 font-mono">{item.path_bucket}</td>
                                <td className="px-4 py-3">{item.operation}</td>
                                <td className="px-4 py-3 font-bold text-orange-700">{formatToPercent(item.baseline_score)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {(fileBaselineData?.items || []).length === 0 && <div className="p-6 text-center text-slate-400">No file baselines found.</div>}
                      </div>
                    </div>
                  )}

                  {memorySubTab === 'behavior' && (
                    <div className="rounded-xl border border-slate-100 bg-white overflow-hidden">
                      <div className="px-5 py-4 border-b border-slate-100">
                        <h4 className="text-sm font-bold text-slate-700">Behavior Confidence</h4>
                        <p className="text-xs text-slate-400">Combined memory confidence history.</p>
                      </div>
                      <div className="overflow-auto max-h-[520px]">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                            <tr><th className="px-4 py-3">Process</th><th className="px-4 py-3">Score</th><th className="px-4 py-3">Label</th><th className="px-4 py-3">Time</th></tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(behaviorConfidenceData?.items || []).map((item, index) => (
                              <tr key={`${item.process_name}-${item.created_at}-${index}`}>
                                <td className="px-4 py-3 font-mono">{item.process_name}</td>
                                <td className="px-4 py-3 font-bold text-purple-700">{formatToPercent(item.confidence_score)}</td>
                                <td className="px-4 py-3">{item.label}</td>
                                <td className="px-4 py-3 text-slate-500">{item.created_at ? new Date(item.created_at).toLocaleString() : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {(behaviorConfidenceData?.items || []).length === 0 && <div className="p-6 text-center text-slate-400">No behavior confidence history found.</div>}
                      </div>
                    </div>
                  )}

                  {memorySubTab === 'reputation' && (
                    <div className="rounded-xl border border-slate-100 bg-white overflow-hidden">
                      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-bold text-slate-700">Fleet Reputation Details</h4>
                          <p className="text-xs text-slate-400">Read-only cross-platform process reputation rows.</p>
                        </div>
                        <span className="text-[10px] font-bold uppercase px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">Context Only</span>
                      </div>
                      <div className="overflow-auto max-h-[520px]">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                            <tr><th className="px-4 py-3">Platform</th><th className="px-4 py-3">Subject</th><th className="px-4 py-3">Safe</th><th className="px-4 py-3">Threat</th><th className="px-4 py-3">Seen</th><th className="px-4 py-3">Penalty</th><th className="px-4 py-3">Label</th><th className="px-4 py-3">Last Seen</th></tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {deviceReputationItems.map((item, index) => (
                              <tr key={`${item.platform}-${`${item.process_name || 'unknown'} :: ${item.exe_path || ''} :: ${item.parent_name || 'unknown'}`}-${index}`} className="hover:bg-slate-50">
                                <td className="px-4 py-3 font-bold text-slate-700">{item.platform}</td>
                                <td className="px-4 py-3 font-mono text-[11px] text-slate-600 max-w-[320px] truncate">{`${item.process_name || 'unknown'} :: ${item.exe_path || ''} :: ${item.parent_name || 'unknown'}`}</td>
                                <td className="px-4 py-3 text-green-700 font-bold">{formatToPercent(item.safe_score)}</td>
                                <td className="px-4 py-3 text-red-700 font-bold">{formatToPercent(item.threat_score)}</td>
                                <td className="px-4 py-3">{item.seen_count}</td>
                                <td className="px-4 py-3 text-orange-700 font-bold">{formatToPercent(item.rollback_penalty)}</td>
                                <td className="px-4 py-3">{item.label}</td>
                                <td className="px-4 py-3 text-slate-500">{item.last_seen ? new Date(item.last_seen).toLocaleString() : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {deviceReputationItems.length === 0 && <div className="p-8 text-center text-slate-400">No fleet reputation rows found.</div>}
                      </div>
                    </div>
                  )}

                  {memorySubTab === 'anomaly' && (
                    <div className="rounded-xl border border-slate-100 bg-white overflow-hidden">
                      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-bold text-slate-700">Baseline Anomaly History</h4>
                          <p className="text-xs text-slate-400">Context-only anomaly signals captured from this selected agent.</p>
                        </div>
                        <span className="text-[10px] font-bold uppercase px-3 py-1 rounded-full bg-red-50 text-red-700 border border-red-100">Context Only</span>
                      </div>
                      <div className="overflow-auto max-h-[520px]">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                            <tr><th className="px-4 py-3">Process</th><th className="px-4 py-3">Score</th><th className="px-4 py-3">Label</th><th className="px-4 py-3">Reasons</th><th className="px-4 py-3">Time</th></tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(baselineAnomalyData?.items || []).map((item, index) => (
                              <tr key={`${item.process_name}-${item.created_at}-${index}`} className="hover:bg-slate-50">
                                <td className="px-4 py-3 font-mono">{item.process_name}</td>
                                <td className="px-4 py-3 font-bold text-red-700">{formatToPercent(item.anomaly_score)}</td>
                                <td className="px-4 py-3">{item.label}</td>
                                <td className="px-4 py-3 text-slate-500 max-w-[420px] truncate">{(item.reasons || []).join(', ') || '-'}</td>
                                <td className="px-4 py-3 text-slate-500">{item.created_at ? new Date(item.created_at).toLocaleString() : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {(baselineAnomalyData?.items || []).length === 0 && <div className="p-6 text-center text-slate-400">No baseline anomaly history found.</div>}
                      </div>
                    </div>
                  )}

                  {memorySubTab === 'clusters' && (
                    <div className="rounded-xl border border-slate-100 bg-white overflow-hidden">
                      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-bold text-slate-700">Threat Pattern Clusters</h4>
                          <p className="text-xs text-slate-400">Dynamic recurrence intervals, frequency tracking, and trust optimizations.</p>
                        </div>
                        <span className="text-[10px] font-bold uppercase px-3 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">Intelligence Only</span>
                      </div>
                      <div className="overflow-auto max-h-[520px]">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                            <tr>
                              <th className="px-4 py-3">Cluster ID</th>
                              <th className="px-4 py-3">Rule Name</th>
                              <th className="px-4 py-3">Maturity</th>
                              <th className="px-4 py-3">Recurrence Score</th>
                              <th className="px-4 py-3">Seen Count</th>
                              <th className="px-4 py-3">Cluster Score</th>
                              <th className="px-4 py-3">MITRE</th>
                              <th className="px-4 py-3">Process</th>
                              <th className="px-4 py-3">Path Bucket</th>
                              <th className="px-4 py-3">Last Seen</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(clustersData?.items || []).map((item, index) => {
                              const maturity = item.cluster_maturity || 'new';
                              const score = item.cluster_score ?? 0;
                              
                              const maturityColors = 
                                maturity === 'dominant' ? 'bg-red-100 text-red-700 border-red-200' :
                                maturity === 'persistent' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                                maturity === 'established' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                                maturity === 'emerging' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                                maturity === 'repeated' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                'bg-slate-100 text-slate-600 border-slate-200';

                              const scoreColor = 
                                score >= 0.70 ? 'text-red-700 font-extrabold' :
                                score >= 0.40 ? 'text-orange-700 font-bold' :
                                score > 0 ? 'text-blue-700 font-bold' :
                                'text-slate-400';

                              return (
                                <tr key={`${item.cluster_id}-${index}`} className="hover:bg-slate-50 transition-colors">
                                  <td className="px-4 py-3 font-mono font-bold text-slate-500">{item.cluster_id}</td>
                                  <td className="px-4 py-3 font-semibold text-slate-700">{item.rule_name || '-'}</td>
                                  <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${maturityColors}`}>
                                      {maturity}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center space-x-2">
                                      <span className="font-mono font-bold text-slate-700">{(item.recurrence_score * 100).toFixed(0)}%</span>
                                      <div className="w-16 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                                        <div 
                                          className="bg-purple-600 h-1.5 rounded-full" 
                                          style={{ width: `${(item.recurrence_score * 100)}%` }}
                                        />
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 font-bold text-slate-800">{item.seen_count}</td>
                                  <td className={`px-4 py-3 ${scoreColor}`}>{formatToPercent(score)}</td>
                                  <td className="px-4 py-3 font-mono text-slate-600">{item.mitre_technique || '-'}</td>
                                  <td className="px-4 py-3 font-mono font-bold text-slate-600">{item.process_name || '-'}</td>
                                  <td className="px-4 py-3 font-mono text-slate-500">{item.path_bucket || '-'}</td>
                                  <td className="px-4 py-3 text-slate-500">{item.last_seen ? new Date(item.last_seen).toLocaleString() : '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {(clustersData?.items || []).length === 0 && <div className="p-8 text-center text-slate-400">No threat pattern clusters found.</div>}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* TRAINING REVIEW TAB */}
        {activeTab === 'training' && (
          <div className="space-y-6 animate-in fade-in duration-500">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-800 flex items-center">
                    <Brain className="w-5 h-5 mr-2 text-purple-600" />
                    Training Candidate Review
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Human review queue for memory candidates from this selected agent. Approval does not start training.
                  </p>
                </div>
                <span className="text-[10px] font-bold uppercase px-3 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                  Review Only
                </span>
              </div>

              {trainingCandidatesLoading ? (
                <div className="p-10 text-center text-slate-400">Loading training candidates...</div>
              ) : !(trainingCandidatesData?.items || []).length ? (
                <div className="p-12 text-center flex flex-col items-center justify-center">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800">No pending training candidates</h3>
                  <p className="text-slate-500 text-sm mt-1">
                    This device has no memory candidates waiting for review.
                  </p>
                </div>
              ) : (
                <div className="overflow-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase">
                      <tr>
                        <th className="px-4 py-3">Subject</th>
                        <th className="px-4 py-3">Safe</th>
                        <th className="px-4 py-3">Threat</th>
                        <th className="px-4 py-3">Penalty</th>
                        <th className="px-4 py-3">Eligibility</th>
                        <th className="px-4 py-3">Reason</th>
                        <th className="px-4 py-3 text-right">Review</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(trainingCandidatesData?.items || []).map((item) => (
                        <tr key={item.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-mono text-[11px] text-slate-700 max-w-[360px] truncate">
                            {item.subject_key}
                          </td>
                          <td className="px-4 py-3 text-green-700 font-bold">{item.safe_score}</td>
                          <td className="px-4 py-3 text-red-700 font-bold">{item.threat_score}</td>
                          <td className="px-4 py-3 text-orange-700 font-bold">{item.rollback_penalty}</td>
                          <td className="px-4 py-3 text-purple-700 font-bold">{item.eligibility_score}</td>
                          <td className="px-4 py-3 text-slate-500 max-w-[260px] truncate">
                            {item.reason || '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                disabled={reviewTrainingCandidateMutation.isLoading}
                                onClick={() => reviewTrainingCandidateMutation.mutate({
                                  id: item.id,
                                  review_status: 'approved',
                                  note: 'Approved from admin device training review. Training not auto-started.',
                                })}
                                className="px-3 py-1.5 rounded-md bg-green-50 text-green-700 border border-green-100 hover:bg-green-100 font-bold"
                              >
                                Approve
                              </button>
                              <button
                                disabled={reviewTrainingCandidateMutation.isLoading}
                                onClick={() => reviewTrainingCandidateMutation.mutate({
                                  id: item.id,
                                  review_status: 'rejected',
                                  note: 'Rejected from admin device training review.',
                                })}
                                className="px-3 py-1.5 rounded-md bg-red-50 text-red-700 border border-red-100 hover:bg-red-100 font-bold"
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* SETTINGS TAB (Real Data) */}
        {activeTab === 'settings' && (
           <div className="animate-in fade-in duration-500">
             <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center">
                   <SettingsIcon className="w-5 h-5 mr-2 text-slate-600" /> Active Policies
                </h3>
                {Object.keys(settings).length === 0 ? (
                    <div className="text-center text-slate-400 py-8">No policy data reported yet.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       {Object.entries(settings).map(([key, value]) => (
                          // Filter out internal timestamp keys
                          key !== 'timestamp_utc' && <SettingCard key={key} label={key} value={value} />
                       ))}
                    </div>
                )}
             </div>
           </div>
        )}

      </div>
    </div>
  );
};

export default DeviceDetailPage;